import useLocaleSync from "@/hooks/useLocaleSync";
import { QUERY_KEYS } from "@/libraries/constant";
import {
	isAuthenticating,
	isMpisAccount,
	isSsoAccount,
} from "@/libraries/helper";
import { getMemberHomeQueryOptions, getMessageUnreadOptions } from "@/queries";
import { AuthWebviewSchema } from "@/schemas";
import type { MemberProfileResponse } from "@/types";
import { onlineManager } from "@tanstack/react-query";
import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { getProfileLoader } from "../-functions/loader/profile";
import OneSignalProvider from "./-providers/OneSignal";

export const Route = createFileRoute("/dashboard")({
	loader: async ({ context }) => {
		const getMemberProfile = (await getProfileLoader(
			context,
		)) as MemberProfileResponse;
		if (getMemberProfile?.user && !isMpisAccount() && !isSsoAccount()) {
			const { user } = getMemberProfile;
			if (!user.email) {
				return redirect({
					to: "/create-account",
					replace: true,
				});
			}
			if (!user.phone_number && !user.verified_at) {
				return redirect({
					to: "/create-account/verify-mobile",
					replace: true,
				});
			}
			if (
				!user.country ||
				!user.fullname ||
				!user.hospital?.name ||
				!user.registration_number
			) {
				return redirect({
					to: "/create-account/professional-credentials",
					replace: true,
				});
			}
			if (typeof user.directory === "undefined") {
				return redirect({
					to: "/create-account/professional-profile",
					replace: true,
				});
			}
			if (
				!user.patient_data_protection_policy ||
				!user.tos_agreement ||
				!user.cookie_policy_agreement ||
				!user.privacy_policy_agreement
			) {
				return redirect({
					to: "/create-account/cookie-credentials",
					replace: true,
				});
			}
		}
		if (onlineManager.isOnline()) {
			return [
				getMemberProfile,
				context.queryClient.ensureQueryData(getMessageUnreadOptions()),
				context.queryClient.ensureQueryData(getMemberHomeQueryOptions()),
			];
		}
		return [
			getMemberProfile,
			context.queryClient.getQueryData([QUERY_KEYS.GET_UNREADS]),
			context.queryClient.getQueryData([QUERY_KEYS.GET_MEMBER_HOME]),
		];
	},
	validateSearch: zodSearchValidator(AuthWebviewSchema),
	beforeLoad: async () => {
		if (!isAuthenticating()) {
			throw redirect({
				to: "/",
				replace: true,
			});
		}
	},
	component: RouteComponent,
});

function RouteComponent() {
	useLocaleSync();
	return (
		<OneSignalProvider>
			<Outlet />
		</OneSignalProvider>
	);
}
